import { defineFieldAdapter, type FieldTransformers } from '@libs/shared/frontend/helpers/define-field-adapter.helper';
import {
  entries as rEntries,
  isNonNullish as rIsNonNullish,
  map as rMap,
  mapValues as rMapValues,
  piped as rPiped,
  when as rWhen,
  pipe as rPipe,
} from 'remeda';

import { LOCALE } from '@/constants/locale';
import { CampaignRequestProgramStageEnum, type components } from '@/libs/openapi';
import { toLocalDate, toLowerDenomination, toUpperDenomination } from '@/utils/convert.util';
import { formatDate, formatDateInTimezone, formatEnumValue, formatNumber } from '@/utils/format.util';

type ProgramResponseDto = components['schemas']['CampaignRequestProgramResponseDto'];

const formatMonthKey = (key: string): string => {
  const [ year, month ] = key.split('-');
  return new Intl.DateTimeFormat(LOCALE, { month: 'short', year: 'numeric' }).format(new Date(Number(year), Number(month) - 1));
};

type ProgramDateRow = Pick<ProgramResponseDto, 'startDate' | 'endDate' | 'timezone'>;

interface ProgramDateFieldTransforms {
  forLabel: string;
  forFormValue: string;
}

export const fromApiCampaignRequestProgramFieldAdapter = defineFieldAdapter({
  fields: {
    stage: formatEnumValue(CampaignRequestProgramStageEnum),
    createdAt: formatDate('withTime'),
    updatedAt: formatDate('withTime'),
    totalBudget: {
      forLabel: rWhen(rIsNonNullish<number | undefined>, rPiped(toUpperDenomination, formatNumber('currency'))),
      forFormValue: rWhen(rIsNonNullish<number | undefined>, toUpperDenomination),
    },
    cost: {
      forLabel: rWhen(rIsNonNullish<number | undefined>, rPiped(toUpperDenomination, formatNumber('currency'))),
      forFormValue: rWhen(rIsNonNullish<number | undefined>, toUpperDenomination),
    },
    clickGoal: rWhen(rIsNonNullish<number | undefined>, formatNumber('decimal')),
    stageTransitions: rMap(formatEnumValue(CampaignRequestProgramStageEnum)),
    monthlyBudget: {
      forLabel: rWhen(
        rIsNonNullish<ProgramResponseDto['monthlyBudget'] | undefined>,
        (value) => rPipe(
          value,
          rEntries(),
          rMap(([ key, v ]) => ({
            key,
            label: formatMonthKey(key),
            value: rPipe(v, toUpperDenomination, formatNumber('currency')),
          })),
        ),
      ),
      forFormValue: rWhen(
        rIsNonNullish<ProgramResponseDto['monthlyBudget'] | undefined>,
        rMapValues<ProgramResponseDto['monthlyBudget'], number>(toUpperDenomination),
      ),
    },
  } satisfies FieldTransformers<ProgramResponseDto>,
  meta: {
    getStartDateRelativeToTimezone: (row: ProgramDateRow): ProgramDateFieldTransforms | undefined => {
      if (!row.startDate) {
        return undefined;
      }
      return {
        forLabel: formatDateInTimezone('full', row.timezone)(row.startDate),
        forFormValue: toLocalDate(row.startDate, row.timezone),
      };
    },
    getEndDateRelativeToTimezone: (row: ProgramDateRow): ProgramDateFieldTransforms | undefined => {
      if (!row.endDate) {
        return undefined;
      }
      return {
        forLabel: formatDateInTimezone('full', row.timezone)(row.endDate),
        forFormValue: toLocalDate(row.endDate, row.timezone),
      };
    },
  },
}, 'strict');

export const toApiProgramFieldAdapter = defineFieldAdapter({
  fields: {
    startDate: rWhen(rIsNonNullish<string | undefined>, (v: string) => v),
    endDate: rWhen(rIsNonNullish<string | undefined>, (v: string) => v),
    totalBudget: rWhen(rIsNonNullish<number | undefined>, toLowerDenomination),
    cost: rWhen(rIsNonNullish<number | undefined>, toLowerDenomination),
    monthlyBudget: rWhen(
      rIsNonNullish<ProgramResponseDto['monthlyBudget'] | undefined>,
      (budget) => rPipe(
        budget,
        rMapValues(toLowerDenomination),
      ),
    ),
  } satisfies FieldTransformers<Partial<ProgramResponseDto>>,
}, 'loose');
